'use client';

import {
  Field,
  Input,
  Label,
  InputProps,
  Description
} from '@headlessui/react';
import InputMask from '@mona-health/react-input-mask';
import { ChangeEvent, ReactNode, Ref, useEffect, useState } from 'react';

import { styles } from './UiKitInputBase.styles';

type BaseInputProps = Omit<InputProps, 'invalid' | 'onChange' | 'className'>;

interface Selection {
  start: number;
  end: number;
}

interface InputState {
  value: string;
  selection: Selection | null;
  enteredString?: string;
}

interface BeforeMaskedStateChangeStates {
  previousState?: InputState;
  currentState?: InputState;
  nextState: InputState;
}

export interface UiKitInputBaseProps extends BaseInputProps {
  className?: string;
  classNameInput?: string;
  label?: string;
  placeholderWithLabel?: boolean;
  helperText?: string;
  error?: boolean;
  success?: boolean;
  /**
   * Иконка справа.
   * Ожидается svg без stroke (в этом свойстве определен цвет) в размере 24x24
   */
  icon?: ReactNode;
  onChange?: (value: string) => void;
  beforeMaskedStateChange?: (
    state: BeforeMaskedStateChangeStates
  ) => InputState;
  ref?: Ref<HTMLInputElement>;
  /**
   * Маска для валидации.
   * Например: '+7 (999) 999-99-99'
   */
  mask?: string | (string | RegExp)[];
}

export function UiKitInputBase({
  label,
  helperText,
  error,
  success,
  icon,
  onChange,
  beforeMaskedStateChange,
  required,
  placeholder = '',
  placeholderWithLabel,
  ref,
  className,
  classNameInput,
  value: initialValue = '',
  ...props
}: UiKitInputBaseProps) {
  const slots = styles({ error, success, icon: !!icon, withLabel: !!label });

  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
    onChange?.(event.target.value);
  };

  const isHasMask = 'mask' in props && props.mask;

  const InputComponent = isHasMask ? InputMask : Input;
  const effectivePlaceholder =
    label && !placeholderWithLabel ? ' ' : placeholder;

  return (
    <Field className={slots.base({ className })}>
      <div className={slots.wrapper()}>
        <InputComponent
          className={slots.input({
            withLabel: !!label,
            className: classNameInput
          })}
          invalid={isHasMask ? undefined : error}
          onChange={handleChange}
          placeholder={effectivePlaceholder}
          ref={ref}
          value={value}
          {...(isHasMask ? { beforeMaskedStateChange } : {})}
          {...props}
        />
        <Label className={slots.label()}>
          {label}
          {required && <span className={slots.required()}>*</span>}
        </Label>
        {icon && <div className={slots.iconContainer()}>{icon}</div>}
      </div>
      {helperText && (
        <Description
          as="div"
          className={slots.helperText()}
        >
          {helperText}
        </Description>
      )}
    </Field>
  );
}
